import { useAuth0 } from '@auth0/auth0-react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import CameraOutdoorIcon from '@mui/icons-material/CameraOutdoor';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import RouterIcon from '@mui/icons-material/Router';
import SensorsIcon from '@mui/icons-material/Sensors';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import TvIcon from '@mui/icons-material/Tv';
import {
  alpha,
  Badge,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { styled, type Theme, type CSSObject, useTheme } from '@mui/material/styles';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCheckPermissions } from '../../auth/index.js';
import { toggleColorMode } from '../../redux/reducers/common.js';
import { capitalize } from '../../utils/index.js';
import EventsList from '../Events/EventsList.js';
import Logo from '../Logo.js';

export const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  height: '100vh',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  height: '100vh',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
  height: '100vh',
}));

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  permission: boolean;
  exact: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

interface NavigationDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const NavigationDrawer: FC<NavigationDrawerProps> = ({ open, setOpen }) => {
  const [activeEventsCount, setActiveEventsCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user: { nickname } = {} } = useAuth0();
  const {
    canReadEvent,
    canReadFeed,
    canReadPatient,
    canReadCode,
    canManageRouter,
    canManageCamera,
    canReadLog,
    canManageUsers,
    canReadActivityEvent,
  } = useCheckPermissions();
  const theme = useTheme();
  const dispatch = useDispatch();

  const sections: NavSection[] = useMemo(
    () => [
      {
        label: 'Monitoring',
        items: [
          { label: 'Home', to: '/', icon: <HomeIcon />, permission: true, exact: true },
          { label: 'Streams', to: '/streams', icon: <TvIcon />, permission: canReadFeed && canReadPatient, exact: true },
          { label: 'Patients', to: '/patients', icon: <PeopleIcon />, permission: canReadPatient, exact: false },
          {
            label: 'Logs & Reports',
            to: '/logs',
            icon: <ReceiptLongTwoToneIcon />,
            permission: canReadLog && canReadPatient,
            exact: true,
          },
          {
            label: 'AI Usage',
            to: '/ai-usage',
            icon: <AutoAwesomeIcon />,
            permission: canReadActivityEvent,
            exact: true,
          },
        ],
      },
      {
        label: 'Devices',
        items: [
          { label: 'Cameras', to: '/cameras', icon: <CameraOutdoorIcon />, permission: canManageCamera, exact: true },
          { label: 'NVRs', to: '/nvrs', icon: <StorageIcon />, permission: canManageCamera, exact: true },
          { label: 'Routers', to: '/routers', icon: <RouterIcon />, permission: canManageRouter, exact: true },
          { label: 'Media Status', to: '/media-status', icon: <SensorsIcon />, permission: canManageCamera, exact: true },
        ],
      },
      {
        label: 'Admin',
        items: [
          { label: 'Codes & Categories', to: '/codes', icon: <SettingsIcon />, permission: canReadCode, exact: true },
          { label: 'Monitors', to: '/monitors', icon: <CoPresentIcon />, permission: canManageUsers, exact: true },
        ],
      },
    ],
    [
      canReadFeed,
      canReadPatient,
      canReadCode,
      canManageCamera,
      canManageRouter,
      canReadLog,
      canManageUsers,
      canReadActivityEvent,
    ],
  );

  const renderItem = (item: NavItem) => {
    const isActive = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
    const button = (
      <ListItemButton
        onClick={() => navigate(item.to)}
        sx={{
          minHeight: 44,
          justifyContent: open ? 'initial' : 'center',
          px: 2.5,
          position: 'relative',
          ...(isActive && {
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.08 : 0.16),
            color: theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: 1,
              bgcolor: theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
            },
            '& .MuiListItemIcon-root': {
              color: theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
            },
          }),
          '&:hover': {
            bgcolor: isActive
              ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22)
              : 'action.hover',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 2.5 : 'auto',
            justifyContent: 'center',
            color: isActive ? undefined : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 600 : 500 }}
          sx={{ opacity: open ? 1 : 0 }}
        />
      </ListItemButton>
    );
    return (
      <ListItem key={item.label} disablePadding sx={{ display: 'block' }}>
        {open ? button : <Tooltip title={item.label} placement="right" arrow>{button}</Tooltip>}
      </ListItem>
    );
  };

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        <Logo />
        <IconButton onClick={() => setOpen(false)}>
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />

      <List sx={{ pt: 0 }}>
        {nickname ? (
          <ListItem alignItems="center" sx={{ px: 2.5, py: 1.25 }}>
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 2.5 : 'auto', justifyContent: 'center' }}>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={capitalize(nickname)}
              primaryTypographyProps={{ variant: 'subtitle2' }}
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItem>
        ) : null}
        <Divider />

        {sections.map((section, sIdx) => {
          const visibleItems = section.items.filter((i) => i.permission);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label}>
              {sIdx > 0 && !open && <Divider sx={{ my: 0.5 }} />}
              {open && (
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    px: 2.5,
                    mt: 1.5,
                    mb: 0.25,
                    color: 'text.secondary',
                    opacity: 0.6,
                  }}
                >
                  {section.label}
                </Typography>
              )}
              {visibleItems.map(renderItem)}
            </div>
          );
        })}

        {canReadEvent && !open ? (
          <>
            <Divider sx={{ my: 0.5 }} />
            <ListItem disablePadding sx={{ display: 'block' }}>
              <Tooltip title="Urgent duties & upcoming tasks" placement="right" arrow>
                <ListItemButton
                  onClick={() => setOpen(true)}
                  sx={{ minHeight: 44, justifyContent: 'center', px: 2.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'text.secondary' }}>
                    <Badge badgeContent={activeEventsCount} color="error">
                      <MonitorHeartIcon />
                    </Badge>
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </>
        ) : null}
      </List>

      {canReadEvent && open ? (
        <>
          <Divider />
          <EventsList setActiveEventsCount={setActiveEventsCount} />
        </>
      ) : null}

      <div style={{ marginTop: 'auto' }}>
        <Divider />
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => dispatch(toggleColorMode())}
            sx={{ minHeight: 44, justifyContent: open ? 'initial' : 'center', px: 2.5 }}
          >
            <ListItemIcon
              sx={{ minWidth: 0, mr: open ? 2.5 : 'auto', justifyContent: 'center', color: 'text.secondary' }}
            >
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </ListItemIcon>
            <ListItemText
              primary={`${capitalize(theme.palette.mode)} mode`}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => logout({ returnTo: window.location.origin })}
            sx={{ minHeight: 44, justifyContent: open ? 'initial' : 'center', px: 2.5 }}
          >
            <ListItemIcon
              sx={{ minWidth: 0, mr: open ? 2.5 : 'auto', justifyContent: 'center', color: 'text.secondary' }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
      </div>
    </Drawer>
  );
};

export default NavigationDrawer;
